import { KuxProgramType } from '../interfaces';

import { tvpilGetLocalCache } from './caching-service';
import { checkEpgCacheLimit, EPG_PREFIX, epgCache, getEpgHoursPartial } from './epg-cache';
import { coverUpProgramsDay } from './epg-cache.helper';
import { EpgRequestArgs } from './epg-request';

interface EpgCache {
  data: KuxProgramType[];
  time: number;
  id: number;
}

interface EpgCacheDays {
  cachedEpg: KuxProgramType[];
  cachedIdList: number[];
  noCachedIdList: number[];
}

const HOUR_MS = 60 * 60 * 1000;

const getEpgCacheKey = (startTime: number, channelId?: number, loadMosaicPrograms?: boolean) =>
  `${EPG_PREFIX}${startTime}_${channelId}${loadMosaicPrograms ? '_mosaic' : ''}`;

export const getEpgCacheDays = async (data: EpgRequestArgs): Promise<EpgCacheDays> => {
  const completeIdList = data.linearIdList ?? [];
  const cache = tvpilGetLocalCache();
  const noCachedIdList: number[] = [];
  for (const channelId of completeIdList) {
    const cacheKey = getEpgCacheKey(data.startTime, channelId, data.loadMosaicPrograms);

    if (data.forceUpdate || !epgCache[cacheKey]) {
      const persistCache = (await cache.getItem(cacheKey)) as EpgCache;
      if (persistCache) {
        epgCache[cacheKey] = persistCache;
        continue;
      }
      if (channelId) {
        noCachedIdList.push(channelId);
      }
    }
  }

  const cachedIdList = completeIdList.filter((channelId) => {
    return (
      !data.forceUpdate && channelId && epgCache[getEpgCacheKey(data.startTime, channelId, data.loadMosaicPrograms)]
    );
  }) as number[];

  let cachedEpg: KuxProgramType[] = [];
  cachedIdList.forEach((channelId) => {
    cachedEpg = cachedEpg.concat(epgCache[getEpgCacheKey(data.startTime, channelId, data.loadMosaicPrograms)].data);
  });

  return { cachedEpg, cachedIdList, noCachedIdList };
};

export const aggregateEpgByDay = (
  linearIdList: number[],
  programs: KuxProgramType[],
  startTime: number,
  loadMosaicPrograms?: boolean,
) => {
  let cachedPrograms: KuxProgramType[] = [];
  const aggregateEpg: { [key: string]: EpgCache } = {};
  linearIdList.forEach(
    (linearId) =>
      (aggregateEpg[getEpgCacheKey(startTime, linearId, loadMosaicPrograms)] = {
        data: [],
        time: Date.now(),
        id: linearId,
      }),
  );

  programs
    .sort((a, b) => a.startDate - b.startDate)
    .forEach((program) => {
      aggregateEpg[
        getEpgCacheKey(startTime, program.permanentProgram?.linearAssetId || program.linearAssetId, loadMosaicPrograms)
      ]?.data.push(program);
    });

  linearIdList.forEach((linearId) => {
    const newPrograms = (aggregateEpg[getEpgCacheKey(startTime, linearId, loadMosaicPrograms)].data =
      coverUpProgramsDay(
        aggregateEpg[getEpgCacheKey(startTime, linearId, loadMosaicPrograms)].data,
        linearId,
        startTime,
        aggregateEpg[getEpgCacheKey(startTime - getEpgHoursPartial() * HOUR_MS, linearId, loadMosaicPrograms)]?.data ||
          [],
        aggregateEpg[getEpgCacheKey(startTime + getEpgHoursPartial() * HOUR_MS, linearId, loadMosaicPrograms)]?.data ||
          [],
      ));
    // const newPrograms = aggregateEpg[getEpgCacheKey(startTime, linearId)`${EPG_PREFIX}${startTime}_${linearId}`].data;
    return (cachedPrograms = [...cachedPrograms, ...newPrograms]);
  });

  return { cachedPrograms, aggregateEpg };
};

export const setEpgCacheDays = (aggregateEpg: { [key: string]: EpgCache }): void => {
  Object.assign(epgCache, aggregateEpg);
  checkEpgCacheLimit();
};

export const getAllCachedPrograms = (): KuxProgramType[] => {
  return Object.keys(epgCache).reduce((acc: KuxProgramType[], key) => {
    acc.push(...epgCache[key].data);

    return acc;
  }, [] as KuxProgramType[]);
};
